import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Empty,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  BellOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SoundOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { alertApi } from "../services/alertApi";
import { AMP_PLATFORM } from "../utils/ampAlertHelpers";
import { useTheme } from "../context/ThemeContext";

const { Text } = Typography;

function normalizeState(raw) {
  const s = String(raw || "").toLowerCase();
  if (s === "active") return "firing";
  return s || "unknown";
}

function stateColor(state) {
  switch (normalizeState(state)) {
    case "firing":
      return "error";
    case "pending":
      return "warning";
    case "error":
      return "magenta";
    case "nodata":
      return "default";
    default:
      return "processing";
  }
}

function severityColor(severity) {
  switch (String(severity || "").toLowerCase()) {
    case "critical":
      return "red";
    case "warning":
      return "orange";
    case "info":
      return "blue";
    default:
      return "default";
  }
}

function alertDisplayName(row) {
  return (
    row?.title ||
    row?.name ||
    row?.labels?.rulename ||
    row?.labels?.alertname ||
    "Unknown"
  );
}

function silenceEndsAtIso(hours = 2) {
  return new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
}

/**
 * Pipeline alerts panel: list, create, edit, delete, silence for one pipeline.
 */
export default function PipelineAlertsTab({
  pipeline,
  clientId,
  awsAccountId,
}) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const pipelineId = pipeline?.pipeline_id || pipeline?.id || "";
  const pipelineName = pipeline?.name || pipeline?.pipeline_name || pipelineId;
  const pipelineType = pipeline?.type || pipeline?.pipeline_type || "";

  const [loading, setLoading] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [rules, setRules] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [creatingId, setCreatingId] = useState(null);
  const [actionKey, setActionKey] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!pipelineId) {
      setActiveAlerts([]);
      setRules([]);
      setError("This pipeline has no pipeline_id; alerts cannot be scoped.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [firingResult, rulesResult] = await Promise.allSettled([
        alertApi.getFiringAlerts({ pipelineId, timeoutMs: 25000, retries: 1 }),
        // Grafana may refresh its API key before listing rules, which can take
        // longer than the normal 20-second request window.
        alertApi.getAlerts({ pipelineId, timeoutMs: 60000 }),
      ]);

      if (rulesResult.status === "fulfilled") {
        const ruleList = Object.values(rulesResult.value || {}).filter(
          (r) => r && typeof r === "object",
        );
        setRules(ruleList);
      }

      if (firingResult.status === "fulfilled") {
        const raw = Array.isArray(firingResult.value?.alerts)
          ? firingResult.value.alerts
          : [];
        // Always dedupe by name + pipeline + state (UID is missing on some Grafana APIs).
        const byKey = new Map();
        raw.forEach((alert) => {
          const labels = alert?.labels || {};
          const state =
            String(alert?.state || "").toLowerCase() === "active"
              ? "firing"
              : String(alert?.state || "").toLowerCase();
          const name = String(
            alert?.name || labels.rulename || labels.alertname || "",
          )
            .trim()
            .toLowerCase();
          if (!name) return;
          const pipeline = String(labels.pipeline_id || "").trim().toLowerCase();
          const platform = String(alert?.platform || "grafana")
            .trim()
            .toLowerCase();
          const key = `${platform}|${name}|${pipeline}|${state}`;
          const existing = byKey.get(key);
          const score = (a) =>
            (a?.alert_rule_uid ? 8 : 0) +
            (a?.activeAt ? 2 : 0) +
            Object.keys(a?.labels || {}).length;
          if (!existing || score(alert) > score(existing)) {
            byKey.set(key, alert);
          }
        });
        setActiveAlerts(Array.from(byKey.values()));
      }

      const errors = [];
      if (firingResult.status === "rejected") {
        errors.push(
          firingResult.reason?.message || "Failed to load active alerts",
        );
      }
      if (rulesResult.status === "rejected") {
        errors.push(
          rulesResult.reason?.message || "Failed to load configured rules",
        );
      }
      setError(errors.length ? errors.join(" · ") : null);
    } catch (err) {
      console.error("[PipelineAlertsTab] load failed", err);
      setError(err?.message || "Failed to load pipeline alerts");
    } finally {
      setLoading(false);
    }
  }, [pipelineId]);

  const loadSuggestions = useCallback(async () => {
    if (!pipelineId) return;
    setSuggestionsLoading(true);
    try {
      const data = await alertApi.getIntelligentAlertSuggestions({
        clientId,
        awsAccountId,
        pipelineId,
        title: pipelineName,
        maxSuggestions: 6,
      });
      setSuggestions(Array.isArray(data?.suggestions) ? data.suggestions : []);
    } catch (err) {
      console.warn("[PipelineAlertsTab] suggestions failed", err);
      setSuggestions([]);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [pipelineId, pipelineName, clientId, awsAccountId]);

  useEffect(() => {
    load();
    loadSuggestions();
  }, [load, loadSuggestions]);

  const pipelineLabels = useMemo(
    () => ({
      pipeline_id: String(pipelineId || ""),
      ...(pipelineName ? { pipeline_name: String(pipelineName) } : {}),
      ...(pipelineType ? { pipeline_type: String(pipelineType) } : {}),
    }),
    [pipelineId, pipelineName, pipelineType],
  );

  const findRuleForAlert = useCallback(
    (row) => {
      const name = alertDisplayName(row).trim().toLowerCase();
      const uid = String(row?.alert_rule_uid || row?.alert_id || "").trim();
      return (
        rules.find(
          (r) =>
            (uid &&
              (String(r.alert_id || "") === uid ||
                String(r.id || "") === uid)) ||
            String(r.title || "")
              .trim()
              .toLowerCase() === name,
        ) || null
      );
    },
    [rules],
  );

  const handleCreateSuggestion = async (suggestion) => {
    const payload = {
      ...(suggestion?.create_payload || {}),
      extra_labels: {
        ...(suggestion?.create_payload?.extra_labels || {}),
        ...pipelineLabels,
      },
    };
    if (!payload.title) {
      message.error("Suggestion is missing a title");
      return;
    }
    setCreatingId(suggestion.suggestion_id || payload.title);
    try {
      const resp = await alertApi.createAlert(payload);
      if (resp?.platform === "amp") {
        message.success(
          resp?.message || "Alert created in AMP (Grafana at capacity)",
        );
      } else {
        message.success("Alert created for this pipeline");
      }
      await load();
      await loadSuggestions();
    } catch (err) {
      message.error(err?.message || "Failed to create alert");
    } finally {
      setCreatingId(null);
    }
  };

  const handleCustomCreate = () => {
    const q = new URLSearchParams();
    q.set("create", "1");
    if (pipelineId) q.set("pipelineId", pipelineId);
    if (pipelineName) q.set("pipelineName", pipelineName);
    if (pipelineType) q.set("pipelineType", pipelineType);
    if (awsAccountId) q.set("accountId", awsAccountId);
    if (clientId) q.set("clientId", clientId);
    navigate(`/alerts?${q.toString()}`);
  };

  const handleEdit = (row) => {
    const rule = row?.title ? row : findRuleForAlert(row);
    const title = alertDisplayName(rule || row);
    const platform =
      (rule || row)?.platform === AMP_PLATFORM ? AMP_PLATFORM : "grafana";
    const q = new URLSearchParams();
    q.set("view", "rules");
    q.set("edit", "1");
    q.set("alert", title);
    q.set("platform", platform);
    if (pipelineId) q.set("pipelineId", pipelineId);
    navigate(`/alerts?${q.toString()}`);
  };

  const handleDelete = async (row) => {
    const rule = row?.alert_id || row?.title ? row : findRuleForAlert(row);
    const id = rule?.alert_id || rule?.id || row?.alert_rule_uid;
    const title = alertDisplayName(rule || row);
    if (!id && !title) {
      message.error("Cannot delete: missing alert id");
      return;
    }
    const key = `delete:${id || title}`;
    setActionKey(key);
    try {
      const platform = (rule || row)?.platform;
      if (platform === AMP_PLATFORM) {
        await alertApi.deleteAmpAlert(id || title);
      } else {
        await alertApi.deleteAlert(id || title);
      }
      message.success(`Deleted "${title}"`);
      await load();
    } catch (err) {
      message.error(err?.message || "Failed to delete alert");
    } finally {
      setActionKey(null);
    }
  };

  const handleSilence = (row) => {
    const title = alertDisplayName(row);
    const platform =
      row?.platform === AMP_PLATFORM ? AMP_PLATFORM : "grafana";
    Modal.confirm({
      title: "Silence this alert?",
      content: `Silence "${title}" for 2 hours on ${platform === AMP_PLATFORM ? "AMP" : "Grafana"}.`,
      okText: "Silence",
      onOk: async () => {
        const key = `silence:${title}`;
        setActionKey(key);
        try {
          const payload = {
            startsAt: new Date().toISOString(),
            endsAt: silenceEndsAtIso(2),
            createdBy: "clairai-pipeline",
            comment: `Silenced from pipeline ${pipelineName || pipelineId}`,
            matchers: [
              {
                name: "alertname",
                value: title,
                isRegex: false,
              },
            ],
          };
          if (platform === AMP_PLATFORM) {
            await alertApi.createAmpSilence(payload);
          } else {
            await alertApi.createSilence(payload);
          }
          message.success(`Silenced "${title}" for 2 hours`);
          await load();
        } catch (err) {
          message.error(err?.message || "Failed to silence alert");
          throw err;
        } finally {
          setActionKey(null);
        }
      },
    });
  };

  const renderActions = (row, { preferSilence = false } = {}) => {
    const title = alertDisplayName(row);
    const keyBase = row.alert_id || row.alert_rule_uid || title;
    return (
      <Space size={4}>
        {preferSilence && (
          <Tooltip title="Silence 2h">
            <Button
              size="small"
              icon={<SoundOutlined />}
              loading={actionKey === `silence:${title}`}
              onClick={() => handleSilence(row)}
            />
          </Tooltip>
        )}
        <Tooltip title="Edit">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(row)}
          />
        </Tooltip>
        {!preferSilence && (
          <Tooltip title="Silence 2h">
            <Button
              size="small"
              icon={<SoundOutlined />}
              loading={actionKey === `silence:${title}`}
              onClick={() => handleSilence(row)}
            />
          </Tooltip>
        )}
        <Popconfirm
          title="Delete this alert?"
          description={`Remove "${title}" permanently?`}
          okText="Delete"
          okButtonProps={{ danger: true }}
          onConfirm={() => handleDelete(row)}
        >
          <Tooltip title="Delete">
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={actionKey === `delete:${keyBase}`}
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    );
  };

  const activeColumns = [
    {
      title: "Alert",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (name, row) => alertDisplayName({ ...row, name }),
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 90,
      render: (state) => (
        <Tag color={stateColor(state)}>{normalizeState(state)}</Tag>
      ),
    },
    {
      title: "Severity",
      key: "severity",
      width: 90,
      render: (_, row) => {
        const sev = row?.labels?.severity;
        return sev ? <Tag color={severityColor(sev)}>{sev}</Tag> : "—";
      },
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 90,
      render: (p) =>
        p === "amp" ? <Tag color="orange">AMP</Tag> : <Tag>Grafana</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (_, row) => renderActions(row, { preferSilence: true }),
    },
  ];

  const ruleColumns = [
    {
      title: "Rule",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
    },
    {
      title: "Severity",
      dataIndex: "severity",
      key: "severity",
      width: 90,
      render: (sev, row) => {
        const s = sev || row?.labels?.severity;
        return s ? <Tag color={severityColor(s)}>{s}</Tag> : "—";
      },
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 90,
      render: (p) =>
        p === "amp" ? <Tag color="orange">AMP</Tag> : <Tag>Grafana</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (_, row) => renderActions(row),
    },
  ];

  const suggestionColumns = [
    {
      title: "Suggestion",
      dataIndex: "title",
      key: "title",
      ellipsis: true,
      render: (title, row) => (
        <div>
          <div>{title}</div>
          {row?.description ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.description}
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 110,
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={creatingId === (row.suggestion_id || row.title)}
          onClick={() => handleCreateSuggestion(row)}
        >
          Create
        </Button>
      ),
    },
  ];

  if (!pipelineId) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Missing pipeline id"
        description="Alerts can only be listed or created for pipelines that have a pipeline_id."
      />
    );
  }

  return (
    <div style={{ maxHeight: 420, overflowY: "auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Space size={4}>
          <BellOutlined />
          <Text strong>Alerts for this pipeline</Text>
        </Space>
        <Space>
          <Tooltip title="Refresh">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={() => {
                load();
                loadSuggestions();
              }}
              loading={loading}
            />
          </Tooltip>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCustomCreate}
          >
            Create Alert
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 12 }}
        />
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
          <Spin tip="Loading pipeline alerts..." />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Active alerts</div>
          {activeAlerts.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active alerts for this pipeline"
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Table
              size="small"
              rowKey={(r, i) =>
                r.alert_rule_uid || `${r.name}-${r.state}-${i}`
              }
              columns={activeColumns}
              dataSource={activeAlerts}
              pagination={false}
              style={{ marginBottom: 16 }}
            />
          )}

          <div style={{ marginBottom: 8, fontWeight: 600 }}>
            Configured rules
          </div>
          {rules.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No alert rules labeled for this pipeline yet"
              style={{ marginBottom: 16 }}
            />
          ) : (
            <Table
              size="small"
              rowKey={(r) => r.alert_id || r.title}
              columns={ruleColumns}
              dataSource={rules}
              pagination={false}
              style={{ marginBottom: 16 }}
            />
          )}

          <div
            style={{
              marginBottom: 8,
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Suggested alerts
            {suggestionsLoading ? <Spin size="small" /> : null}
          </div>
          {suggestions.length === 0 && !suggestionsLoading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No suggestions right now — use Create Alert for a custom rule"
            />
          ) : (
            <Table
              size="small"
              rowKey={(r) => r.suggestion_id || r.title}
              columns={suggestionColumns}
              dataSource={suggestions}
              pagination={false}
              loading={suggestionsLoading}
            />
          )}

          <Text
            type="secondary"
            style={{
              display: "block",
              marginTop: 12,
              fontSize: 12,
              color: isDark ? "#8b949e" : undefined,
            }}
          >
            Edit opens the Alerts page. Silence lasts 2 hours. Delete removes the
            rule permanently.
          </Text>
        </>
      )}
    </div>
  );
}
